import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Checkbox, Divider, Form, Input, message, Spin, Typography } from 'antd';
import { useAsync } from 'react-async-hook';
import { Banner } from 'components/ui/Banner';
import Html from 'Html';
import { familyService } from 'services';
import commonService from 'services/common.service';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import PageMeta from 'components/PageMeta/PageMeta';
import Card from 'components/Card/Card';
import dayjs from 'dayjs';
import { DATE_FORMAT_SENTENCE } from 'components/AriaDatePicker/AriaDatePicker';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyApplicationAgreement({ application, setApplication, loading, setStep, readOnly = false }) {
  const stepConfig = useAtomValue(stepConfigAtom);

  // State
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  const isAgreementCheckboxEnabled = application?.agreementAccepted;

  const { result: agreements = [], loading: loadingAgreement } = useAsync(async () => {
    if (application?.programType?.id) {
      return await commonService.get(
        `/families/ref-data/agreements/lookup/?typeId=${application?.programType?.id}&branchId=${application?.applicant?.militaryDetails?.branch?.id}`,
      );
    }

    return [];
  }, [application?.programType?.id]);

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.AGREEMENT, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        // Validate form fields
        setSubmitting(true);

        if (!isAgreementCheckboxEnabled && values.agreementAccepted) {
          await familyService.acceptAgreement(application?.id, {
            applicationId: Number(application?.id),
            agreementIds: (agreements || []).map((agr) => agr.id),
          });
        }

        // Update agreementAcceptedBy field
        await familyService.saveApplication({
          ...application,
          agreementAcceptedBy: values.agreementAcceptedBy,
        });

        if (!application.submittedDate) {
          await familyService.submitApplication(application?.id);
        }

        const updatedApplication = await familyService.getApplication(application?.id);
        setApplication(updatedApplication);

        _goToNextStep(updatedApplication);
      } catch (error) {
        message.error('Unable to update application', 5);
        newrelic.noticeError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [isAgreementCheckboxEnabled, application, agreements, readOnly, setApplication, setStep, stepConfig],
  );

  useEffect(() => {
    form.setFieldsValue({
      agreementAccepted: application?.agreementAccepted || false,
      agreementAcceptedBy: application?.agreementAcceptedBy || '',
    });
  }, [application?.agreementAccepted, application?.agreementAcceptedBy, form]);

  if (loading || loadingAgreement) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Acknowledgement and Agreement" />

      <Form form={form} onFinish={onSubmit} layout="vertical" className="space-y-4" disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner question="Almost done" className="my-12" />

          {/* Agreement Content */}
          <Card title="Terms and Conditions" className="mb-8">
            <div className="max-h-96 overflow-y-auto rounded-xl p-6 bg-gray-50">
              {agreements.map(({ id, content }, index) => (
                <div key={id} className="mb-6">
                  <Html html={content} />

                  {index !== agreements.length - 1 && <Divider />}
                </div>
              ))}
            </div>
          </Card>

          {/* Agreement Checkbox */}
          <Card title="Parent/Guardian Acknowledgement" className="mb-8">
            <Form.Item
              name="agreementAccepted"
              valuePropName="checked"
              rules={[
                {
                  validator: (_, value) => {
                    if (!value) {
                      return Promise.reject(new Error('You must agree to the terms and conditions'));
                    }

                    return Promise.resolve();
                  },
                },
              ]}
            >
              <Checkbox className="flex items-start" data-testid="agreement-checkbox">
                <Typography.Text type="secondary">I agree to these terms</Typography.Text>
              </Checkbox>
            </Form.Item>

            <Form.Item
              name="agreementAcceptedBy"
              label="Your full name"
              rules={[
                {
                  required: true,
                  message: 'Please enter your full name',
                },
              ]}
              className={!application?.agreementAcceptedDate ? 'mb-0' : undefined}
            >
              <Input className="w-full max-w-md" data-testid="agreement-name-input" />
            </Form.Item>

            {application?.agreementAcceptedDate && (
              <Alert
                message={
                  <>
                    Accepted Date:{' '}
                    <span className="font-normal">
                      {dayjs(application?.agreementAcceptedDate).format(DATE_FORMAT_SENTENCE)}
                    </span>
                  </>
                }
                type="success"
                showIcon
              />
            )}
          </Card>
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            onClick: () => {
              const previousStep = getPreviousStep(FAMILY_APPLICATION_STEPS.AGREEMENT, application, stepConfig);
              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
            'data-testid': 'agreement-back-button',
          }}
          nextButtonProps={{
            text: readOnly ? 'Next' : 'Submit Application',
            loading: submitting,
            'data-testid': 'agreement-submit-button',
          }}
        />
      </Form>
    </>
  );
}
